import { sessionStorage } from "@/types/session-storage";
import { useCallback, useEffect, useRef, useState } from "react";

interface DashboardStateConfig {
  activeSection: string;
  selectedJurisdictionsKey?: string;
  selectedLicenseTypesKey: string;
  selectedRegionsKey?: string;
  isMapViewKey: string;
  columnFiltersKey: string;
  inactiveRegistrationsColumnFiltersKey?: string;
  unregisteredPropertiesColumnFiltersKey: string;
  inspectionsColumnFiltersKey?: string;
  checksColumnFiltersKey?: string;
  invoicesColumnFiltersKey?: string;
  refundsColumnFiltersKey?: string;
  titleRequestsColumnFiltersKey?: string;
  includeJurisdictions?: boolean;
}

export function useDashboardState(config: DashboardStateConfig) {
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>(
    () =>
      config.selectedJurisdictionsKey
        ? sessionStorage.get(config.selectedJurisdictionsKey, [])
        : [],
  );

  const [selectedLicenseTypes, setSelectedLicenseTypes] = useState<string[]>(
    () => sessionStorage.get(config.selectedLicenseTypesKey, []),
  );

  const [selectedRegions, setSelectedRegions] = useState<string[]>(() =>
    config.selectedRegionsKey
      ? sessionStorage.get(config.selectedRegionsKey, [])
      : [],
  );

  const [isMapView, setIsMapView] = useState(() =>
    sessionStorage.get(config.isMapViewKey, false),
  );

  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    () => sessionStorage.get(config.columnFiltersKey, { active: ["Yes"] }),
  );

  const [
    inactiveRegistrationsColumnFilters,
    setInactiveRegistrationsColumnFilters,
  ] = useState<Record<string, string[]>>(() =>
    config.inactiveRegistrationsColumnFiltersKey
      ? sessionStorage.get(config.inactiveRegistrationsColumnFiltersKey, {})
      : {},
  );

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const [
    unregisteredPropertiesColumnFilters,
    setUnregisteredPropertiesColumnFilters,
  ] = useState<Record<string, string[]>>(() =>
    sessionStorage.get(
      config.unregisteredPropertiesColumnFiltersKey,
      { active: ["Yes"] }, // Show only active unregistered properties by default
    ),
  );

  const [
    unregisteredPropertiesColumnVisibility,
    setUnregisteredPropertiesColumnVisibility,
  ] = useState<Record<string, boolean>>({});

  const [inspectionsColumnFilters, setInspectionsColumnFilters] = useState<
    Record<string, string[]>
  >(() =>
    config.inspectionsColumnFiltersKey
      ? sessionStorage.get(config.inspectionsColumnFiltersKey, {})
      : {},
  );

  const [inspectionsColumnVisibility, setInspectionsColumnVisibility] =
    useState<Record<string, boolean>>({});

  const [checksColumnFilters, setChecksColumnFilters] = useState<
    Record<string, string[]>
  >(() =>
    config.checksColumnFiltersKey
      ? sessionStorage.get(config.checksColumnFiltersKey, {
          status: ["Awaiting Check"],
        })
      : { status: ["Awaiting Check"] },
  );

  const [checksColumnVisibility, setChecksColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const [invoicesColumnFilters, setInvoicesColumnFilters] = useState<
    Record<string, string[]>
  >(() =>
    config.invoicesColumnFiltersKey
      ? sessionStorage.get(config.invoicesColumnFiltersKey, {
          status: ["Payment Pending"],
        })
      : { status: ["Payment Pending"] },
  );

  const [invoicesColumnVisibility, setInvoicesColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const [refundsColumnFilters, setRefundsColumnFilters] = useState<
    Record<string, string[]>
  >(() =>
    config.refundsColumnFiltersKey
      ? sessionStorage.get(config.refundsColumnFiltersKey, {
          allocation_status: ["Needs Allocation"],
        })
      : { allocation_status: ["Needs Allocation"] },
  );

  const [refundsColumnVisibility, setRefundsColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const [titleRequestsColumnFilters, setTitleRequestsColumnFilters] = useState<
    Record<string, string[]>
  >(() =>
    config.titleRequestsColumnFiltersKey
      ? sessionStorage.get(config.titleRequestsColumnFiltersKey, {
          active: ["Yes"],
        })
      : { active: ["Yes"] },
  );

  const [titleRequestsColumnVisibility, setTitleRequestsColumnVisibility] =
    useState<Record<string, boolean>>({});

  const hasAppliedDefaultFilter = useRef(false);

  const { activeSection } = config;

  useEffect(() => {
    if (config.selectedJurisdictionsKey) {
      sessionStorage.set(
        config.selectedJurisdictionsKey,
        selectedJurisdictions,
      );
    }
  }, [selectedJurisdictions, config.selectedJurisdictionsKey]);

  useEffect(() => {
    sessionStorage.set(config.selectedLicenseTypesKey, selectedLicenseTypes);
  }, [selectedLicenseTypes, config.selectedLicenseTypesKey]);

  useEffect(() => {
    if (config.selectedRegionsKey) {
      sessionStorage.set(config.selectedRegionsKey, selectedRegions);
    }
  }, [selectedRegions, config.selectedRegionsKey]);

  useEffect(() => {
    sessionStorage.set(config.isMapViewKey, isMapView);
  }, [isMapView, config.isMapViewKey]);

  useEffect(() => {
    sessionStorage.set(config.columnFiltersKey, columnFilters);
  }, [columnFilters, config.columnFiltersKey]);

  useEffect(() => {
    if (config.inactiveRegistrationsColumnFiltersKey) {
      sessionStorage.set(
        config.inactiveRegistrationsColumnFiltersKey,
        inactiveRegistrationsColumnFilters,
      );
    }
  }, [
    inactiveRegistrationsColumnFilters,
    config.inactiveRegistrationsColumnFiltersKey,
  ]);

  useEffect(() => {
    sessionStorage.set(
      config.unregisteredPropertiesColumnFiltersKey,
      unregisteredPropertiesColumnFilters,
    );
  }, [
    unregisteredPropertiesColumnFilters,
    config.unregisteredPropertiesColumnFiltersKey,
  ]);

  useEffect(() => {
    if (config.inspectionsColumnFiltersKey) {
      sessionStorage.set(
        config.inspectionsColumnFiltersKey,
        inspectionsColumnFilters,
      );
    }
  }, [inspectionsColumnFilters, config.inspectionsColumnFiltersKey]);

  useEffect(() => {
    if (config.checksColumnFiltersKey) {
      sessionStorage.set(config.checksColumnFiltersKey, checksColumnFilters);
    }
  }, [checksColumnFilters, config.checksColumnFiltersKey]);

  useEffect(() => {
    if (config.invoicesColumnFiltersKey) {
      sessionStorage.set(
        config.invoicesColumnFiltersKey,
        invoicesColumnFilters,
      );
    }
  }, [invoicesColumnFilters, config.invoicesColumnFiltersKey]);

  useEffect(() => {
    if (config.refundsColumnFiltersKey) {
      sessionStorage.set(config.refundsColumnFiltersKey, refundsColumnFilters);
    }
  }, [refundsColumnFilters, config.refundsColumnFiltersKey]);

  useEffect(() => {
    if (config.titleRequestsColumnFiltersKey) {
      sessionStorage.set(
        config.titleRequestsColumnFiltersKey,
        titleRequestsColumnFilters,
      );
    }
  }, [titleRequestsColumnFilters, config.titleRequestsColumnFiltersKey]);

  useEffect(() => {
    if (
      activeSection === "unregistered-properties" &&
      !hasAppliedDefaultFilter.current
    ) {
      const hasCustomFilters =
        Object.keys(unregisteredPropertiesColumnFilters).length > 0 &&
        !(
          Object.keys(unregisteredPropertiesColumnFilters).length === 1 &&
          unregisteredPropertiesColumnFilters.active?.includes("Yes")
        );

      if (!hasCustomFilters) {
        setUnregisteredPropertiesColumnFilters({ active: ["Yes"] });
        hasAppliedDefaultFilter.current = true;
      }
    } else if (activeSection !== "unregistered-properties") {
      hasAppliedDefaultFilter.current = false;
    }
  }, [activeSection, unregisteredPropertiesColumnFilters]);

  const handleMapViewChange = useCallback((newMapView: boolean) => {
    setIsMapView(newMapView);
  }, []);

  return {
    selectedJurisdictions,
    setSelectedJurisdictions,
    selectedLicenseTypes,
    setSelectedLicenseTypes,
    selectedRegions,
    setSelectedRegions,
    isMapView,
    columnFilters,
    setColumnFilters,
    inactiveRegistrationsColumnFilters,
    setInactiveRegistrationsColumnFilters,
    columnVisibility,
    setColumnVisibility,
    unregisteredPropertiesColumnFilters,
    setUnregisteredPropertiesColumnFilters,
    unregisteredPropertiesColumnVisibility,
    setUnregisteredPropertiesColumnVisibility,
    inspectionsColumnFilters,
    setInspectionsColumnFilters,
    inspectionsColumnVisibility,
    setInspectionsColumnVisibility,
    checksColumnFilters,
    setChecksColumnFilters,
    checksColumnVisibility,
    setChecksColumnVisibility,
    invoicesColumnFilters,
    setInvoicesColumnFilters,
    invoicesColumnVisibility,
    setInvoicesColumnVisibility,
    refundsColumnFilters,
    setRefundsColumnFilters,
    refundsColumnVisibility,
    setRefundsColumnVisibility,
    titleRequestsColumnFilters,
    setTitleRequestsColumnFilters,
    titleRequestsColumnVisibility,
    setTitleRequestsColumnVisibility,

    handleMapViewChange,
  };
}
